import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext.jsx";
import { useOktaSession } from "../okta/OktaSessionContext.jsx";
import { useProjectOptional } from "../context/ProjectContext.jsx";
import { storeToken } from "../services/tokenVault.js";
import Button from "./ui/Button.jsx";
import Badge from "./ui/Badge.jsx";

function statusVariant(st) {
  const s = String(st || "").toLowerCase();
  if (s === "active") return "active";
  if (s === "complete" || s === "completed") return "complete";
  if (s === "paused") return "paused";
  if (s === "archived") return "archived";
  return "info";
}

function formatShort(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

export default function ResumeProjectModal({ projectSummary, onClose, onDone }) {
  const navigate = useNavigate();
  const { validateSswsToken } = useAuth();
  const { setSession } = useOktaSession();
  const projectCtx = useProjectOptional();
  const [apiToken, setApiToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const handleBackdropPointerDown = () => {
    setShake(true);
    window.setTimeout(() => setShake(false), 400);
  };

  const projectId = projectSummary?.projectId;
  const isEntra = String(projectSummary?.providerId || "").toLowerCase() === "entra";
  const orgUrl = projectSummary?.orgUrl || "";
  const orgName = projectSummary?.orgName || "Project";
  const rp = projectSummary?.roadmapProgress || {};
  const complete = Number(rp.complete) || 0;
  const total = Number(rp.total) || 0;

  const openEntraWorkspace = () => {
    if (!projectId) return;
    navigate(`/project/${encodeURIComponent(projectId)}`);
    onDone?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (isEntra) {
      openEntraWorkspace();
      return;
    }
    const tok = apiToken.trim();
    if (!tok) {
      setError("API token is required.");
      return;
    }
    if (!orgUrl || !projectId) {
      setError("Invalid project.");
      return;
    }
    setLoading(true);
    try {
      await validateSswsToken(orgUrl, tok);
      const res = await fetch("/api/idp/okta/org-audit", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orgUrl, apiToken: tok, projectId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Org audit failed");
      storeToken(orgUrl, tok);
      projectCtx?.setApiToken?.(tok, orgUrl);
      setSession({ orgUrl, apiToken: tok });
      onDone?.();
      onClose?.();
      navigate(`/project/${encodeURIComponent(projectId)}`, {
        state: {
          drift: data.drift ?? null,
          audit: data.audit ?? null,
        },
      });
    } catch (err) {
      setError(err?.message || "Reconnect failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 100,
        background: "#00000088",
        display: "grid",
        placeItems: "center",
        padding: "24px",
        cursor: "default",
      }}
      onPointerDown={handleBackdropPointerDown}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        className={shake ? "shake" : undefined}
        style={{
          width: "100%",
          maxWidth: "480px",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: "var(--bg-surface)",
          border: "1px solid var(--border-bright)",
          borderRadius: "8px",
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div style={{ flexShrink: 0, padding: "24px 32px 0" }}>
          <h2 style={{ fontSize: "20px", fontWeight: 600, marginBottom: "8px" }}>
            Resume — {orgName}
          </h2>
          <p className="text-secondary" style={{ fontSize: "14px", margin: 0 }}>
            {isEntra
              ? "Entra credentials are stored server-side. Open the workspace to continue your assessment."
              : "Enter your API token to reconnect. We never store tokens between sessions."}
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
        >
          <div
            style={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              padding: "20px 32px 8px",
            }}
          >
            <div
              style={{
                padding: "12px",
                borderRadius: "6px",
                background: "var(--bg-elevated)",
                border: "1px solid var(--border)",
                marginBottom: "20px",
                fontSize: "13px",
                color: "var(--text-secondary)",
              }}
            >
              <div style={{ marginBottom: "6px" }}>· Last session: {formatShort(projectSummary?.updatedAt)}</div>
              <div style={{ marginBottom: "6px" }}>
                · Roadmap: {complete}/{total} items complete
              </div>
              <div>
                · Status{" "}
                <Badge variant={statusVariant(projectSummary?.status)}>{projectSummary?.status || "active"}</Badge>
              </div>
            </div>
          {!isEntra ? (
            <>
              <label style={{ display: "block", marginBottom: "8px" }}>
                <span className="text-secondary" style={{ fontSize: "12px" }}>
                  Okta org URL
                </span>
                <input
                  readOnly
                  value={orgUrl}
                  style={{
                    width: "100%",
                    marginTop: "6px",
                    padding: "10px 12px",
                    borderRadius: "4px",
                    border: "1px solid var(--border)",
                    background: "var(--bg-base)",
                    color: "var(--text-dim)",
                    fontSize: "13px",
                  }}
                />
              </label>

              <label style={{ display: "block", marginBottom: "16px" }}>
                <span className="text-secondary" style={{ fontSize: "12px" }}>
                  API token
                </span>
                <div style={{ display: "flex", gap: "8px", marginTop: "6px" }}>
                  <input
                    required
                    type={showToken ? "text" : "password"}
                    value={apiToken}
                    onChange={(e) => setApiToken(e.target.value)}
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      borderRadius: "4px",
                      border: "1px solid var(--border)",
                      background: "var(--bg-input)",
                      color: "var(--text-primary)",
                      fontSize: "14px",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowToken((s) => !s)}
                    style={{
                      background: "var(--bg-elevated)",
                      border: "1px solid var(--border)",
                      borderRadius: "4px",
                      padding: "0 12px",
                      cursor: "pointer",
                      color: "var(--text-secondary)",
                    }}
                  >
                    {showToken ? "Hide" : "Show"}
                  </button>
                </div>
              </label>
            </>
          ) : (
            <p className="text-dim" style={{ fontSize: "12px", marginBottom: "12px" }}>
              Tenant: <span className="font-mono">{orgUrl}</span>
            </p>
          )}

            {error ? (
              <p className="text-critical" style={{ fontSize: "13px", marginBottom: "12px" }}>
                {error}
              </p>
            ) : null}
          </div>

          <div
            style={{
              flexShrink: 0,
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              padding: "16px 32px 24px",
              borderTop: "1px solid var(--border)",
              boxShadow: "0 -8px 24px rgba(0, 0, 0, 0.12)",
            }}
          >
            <Button type="button" variant="ghost" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" loading={loading} disabled={loading}>
              {isEntra ? "Open workspace →" : "Reconnect & Continue →"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
